import { useEffect, useRef } from 'react'

import { Home, List as ListIcon, Settings } from 'lucide-react'
import { usePathname } from 'next/navigation'

import { SplitsLink } from '~/components/Link'
import { CurrencyAmount, List } from '~/components/component-library'
import { DrawerContent } from '~/components/component-library/Dialog'
import { useSettingsNavSections } from '~/components/settings/SettingsSidebar'
import { SidebarSection } from '~/components/sidebar'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org/useActiveOrg'
import { useOrgBalances } from '~/hooks/useBalances'
import { useNavigation } from '~/hooks/useNavigation'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { cn } from '~/utils/tailwind-utils'

export const BottomNavBar = () => {
  const pathname = usePathname()
  const { setGlobalModalState } = useAuth()

  return (
    <div className="pointer-events-none fixed bottom-0 z-30 flex w-full items-center justify-center py-5">
      <div className="pointer-events-auto flex items-center justify-center rounded-full border border-gray-300 bg-white/90 p-1 shadow-lg shadow-black/5 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/90 dark:shadow-gray-950/10">
        <SplitsLink
          path="/"
          className={cn(
            'flex h-12 flex-1 items-center justify-center rounded-full px-5 text-secondary',
            {
              'bg-surface-strong text-primary': pathname === '/',
            },
          )}
        >
          <Home className="size-6" />
        </SplitsLink>
        <button
          onClick={() =>
            setGlobalModalState({
              isOpen: true,
              content: <AccountsDrawer />,
            })
          }
          className={cn(
            'flex h-12 flex-1 items-center justify-center rounded-full px-5 text-secondary',
            {
              'bg-surface-strong text-primary': false,
            },
          )}
        >
          <ListIcon className="size-6" />
        </button>
        <button
          aria-label="Settings"
          onClick={() =>
            setGlobalModalState({
              isOpen: true,
              content: <SettingsDrawer />,
            })
          }
          className={cn(
            'flex h-12 flex-1 items-center justify-center rounded-full px-5 text-secondary',
            {
              'bg-surface-strong text-primary': pathname.includes('/settings'),
            },
          )}
        >
          <Settings className="size-6" />
        </button>
      </div>
    </div>
  )
}

const AccountsDrawer = () => {
  const pathname = usePathname()
  const { closeGlobalModal } = useAuth()
  const navigate = useNavigation()

  const { smartAccounts: orderedSmartAccounts } = useSmartAccounts({
    orderedByValue: true,
  })

  const { org } = useActiveOrg()
  const { balances } = useOrgBalances(org?.id)

  return (
    <DrawerContent
      closeButton={{ enabled: true }}
      dialogTitle={'Accounts'}
      hideTitle={false}
    >
      <div className="w-full px-6">
        <List
          items={orderedSmartAccounts.map((smartAccount) => {
            const totalValue = balances[smartAccount.address]?.totalValue ?? 0
            return {
              key: smartAccount.id,
              isDisabled: pathname.startsWith(
                `/accounts/${smartAccount.address}/`,
              ),
              content: smartAccount.name,
              meta: <CurrencyAmount value={totalValue} layout="inline" />,
              onClick: () => {
                closeGlobalModal()
                navigate(`/accounts/${smartAccount.address}`)
              },
            }
          })}
          cta={{
            label: 'Create account',
            onClick: () => {
              closeGlobalModal()
              navigate('/accounts/create')
            },
          }}
        />
      </div>
    </DrawerContent>
  )
}

const SettingsDrawer = () => {
  const [workspaceSection, ...sections] = useSettingsNavSections()
  const { closeGlobalModal } = useAuth()
  const pathname = usePathname()
  const initialPathnameRef = useRef(pathname)

  // Close drawer when navigating to a new page
  useEffect(() => {
    if (pathname !== initialPathnameRef.current) {
      closeGlobalModal()
    }
  }, [pathname, closeGlobalModal])

  return (
    <DrawerContent
      closeButton={{ enabled: true }}
      dialogTitle={'Settings'}
      hideTitle={false}
    >
      <div className="w-full px-2">
        <SidebarSection items={workspaceSection.items} size="base" />
        {sections.map((section) => (
          <div key={section.title}>
            <hr className="border-gray-150 my-2 dark:border-gray-800" />
            <SidebarSection
              title={section.title}
              items={section.items}
              size="base"
            />
          </div>
        ))}
      </div>
    </DrawerContent>
  )
}
